import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';
import { Star, Heart, Music, Sparkles, ArrowRight, Clock, Users, Calendar, Loader2 } from 'lucide-react';
import api from '../lib/api';

const LOGO = 'https://customer-assets-jt897jd0.emergentagent.net/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/dxzmpn6q_Untitled-1.png';
const PHOTO_TUTUS = 'https://customer-assets-jt897jd0.emergentagent.net/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/obzsqy6d_BB607AA0-4903-424E-827E-3C81C2B401D8.PNG';
const PHOTO_BOY = 'https://customer-assets-jt897jd0.emergentagent.net/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/a21kghxe_75A4DCC4-F952-4B1F-9D19-2B337463B466.PNG';
const PHOTO_HULA = 'https://customer-assets-jt897jd0.emergentagent.net/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/dczkg95q_b-Primatotterinas-024%202.webp';
const PHOTO_STAGE = 'https://customer-assets-jt897jd0.emergentagent.net/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/yl1zsc1z_DSC02647%202.webp';

const TRIAL_URL = '/free-trial?className=Primatotterina';

const BookTrialButton = ({ testId, size = 'lg' }) => (
  <Button
    className={`bg-[#FF007F] hover:bg-[#D6006A] text-white font-black rounded-full shadow-[0_8px_30px_rgba(255,0,127,0.35)] hover:shadow-[0_8px_40px_rgba(255,0,127,0.5)] hover:-translate-y-0.5 transition-all uppercase tracking-wide ${
      size === 'lg' ? 'text-lg px-10 py-7' : 'text-base px-8 py-6'
    }`}
    onClick={() => window.location.href = TRIAL_URL}
    data-testid={testId}
  >
    Book a Free Trial <ArrowRight className="w-5 h-5 ml-2" />
  </Button>
);

const Primatotterinas = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await api.get('/page-content/primatotterinas');
        const sectionsObj = {};
        res.data.sections.forEach(s => {
          sectionsObj[s.key] = s.content;
        });
        setContent(sectionsObj);
      } catch (err) {
        console.error('Failed to load page content:', err);
        setContent(getDefaults());
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const getDefaults = () => ({
    banner_image: '',
    section_image: '',
    age_badge: "Ages 3-5 Years",
    hero_subtitle: "Our magical early years dance programme introduces little ones to the wonderful world of ballet, movement, and creative expression in a fun, nurturing environment.",
    feature_1_title: "Creative Play",
    feature_1_desc: "Learning through imaginative play, stories, and music",
    feature_2_title: "Ballet Basics",
    feature_2_desc: "Introduction to ballet positions, movements, and terminology",
    feature_3_title: "Music & Rhythm",
    feature_3_desc: "Developing musicality and rhythm through dance",
    feature_4_title: "Confidence",
    feature_4_desc: "Building self-esteem and social skills through dance",
    about_title: "A Magical Introduction to Dance",
    about_p1: "Primatotterinas is specially designed for our youngest dancers aged 3-5. Through storytelling, props, and imaginative play, children discover the joy of movement while developing coordination, balance, and musicality.",
    about_p2: "Our experienced teachers create a warm, supportive atmosphere where every child feels valued and encouraged to express themselves creatively. It's the perfect stepping stone to formal dance training.",
    class_duration: "45 mins",
    class_size: "Small",
    class_day: "Saturdays",
    skills_list: "Basic ballet positions (1st, 2nd, 3rd)\nPointing and flexing feet\nPliés and relevés\nMoving to music and rhythm\nFollowing instructions\nWorking as part of a group\nCreative movement and expression\nSimple ballet terminology",
    price: "18",
    price_features: "45-minute class each Saturday\nSmall class sizes\nExperienced teachers\nEnd of term performance",
    what_to_wear: "Pink leotard, pink ballet tights, and pink ballet shoes. Hair should be secured away from the face.",
    what_to_bring: "A water bottle and a smile! Everything else is provided.",
    parent_info: "Parents are welcome to watch from our viewing area. We encourage independence but understand that some children may need time to settle in - that's completely normal!",
    cta_title: "Start Their Dance Journey Today",
    cta_subtitle: "Book a free trial and let your little one experience the magic of Primatotterinas!"
  });

  const c = content || getDefaults();
  const skills = c.skills_list ? c.skills_list.split('\n').filter(s => s.trim()) : [];
  const priceFeatures = c.price_features ? c.price_features.split('\n').filter(s => s.trim()) : [];

  const features = [
    { icon: Sparkles, title: c.feature_1_title, desc: c.feature_1_desc, bg: 'bg-pink-50', border: 'border-pink-200', iconColor: 'text-[#FF007F]' },
    { icon: Star, title: c.feature_2_title, desc: c.feature_2_desc, bg: 'bg-purple-50', border: 'border-purple-200', iconColor: 'text-purple-500' },
    { icon: Music, title: c.feature_3_title, desc: c.feature_3_desc, bg: 'bg-amber-50', border: 'border-amber-200', iconColor: 'text-amber-500' },
    { icon: Heart, title: c.feature_4_title, desc: c.feature_4_desc, bg: 'bg-rose-50', border: 'border-rose-200', iconColor: 'text-rose-500' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fff7fb] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fff7fb] text-neutral-800">
      <SEO page="primatotterinas" />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-pink-100 via-[#fff7fb] to-[#fff7fb]">
        {/* floating sparkles */}
        <Star className="hidden sm:block absolute top-16 left-[8%] w-6 h-6 text-pink-300 animate-pulse" />
        <Sparkles className="hidden sm:block absolute top-32 right-[10%] w-8 h-8 text-purple-300 animate-pulse" />
        <Heart className="hidden sm:block absolute top-64 left-[15%] w-5 h-5 text-rose-300 animate-pulse" />

        <div className="max-w-6xl mx-auto px-4 pt-10 pb-14 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            {/* Logo */}
            <img
              src={LOGO}
              alt="Primatotterinas — Dance, Confidence, Creativity"
              className="w-72 sm:w-96 mx-auto mb-4"
              data-testid="prima-logo"
            />
            <span className="inline-block px-5 py-2 bg-[#FF007F] text-white rounded-full text-sm font-bold mb-5 shadow-md shadow-pink-200">
              {c.age_badge}
            </span>
            <h1 className="sr-only">Primatotterinas</h1>
            <p className="text-base md:text-lg text-neutral-600 max-w-2xl mx-auto mb-7">
              {c.hero_subtitle}
            </p>
            <BookTrialButton testId="prima-book-trial-hero" />
            <p className="mt-3 text-xs text-neutral-500">Free taster class · No commitment · Kents Hill, Milton Keynes</p>
          </motion.div>
        </div>

        {/* Photo collage */}
        <div className="max-w-5xl mx-auto px-4 pb-14 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 items-center">
            {[
              { src: PHOTO_TUTUS, alt: 'Primatotterinas in pink tutus', rot: '-rotate-2' },
              { src: PHOTO_BOY, alt: 'Primatotterinas boy in class', rot: 'rotate-2' },
              { src: PHOTO_HULA, alt: 'Primatotterina performing on stage', rot: '-rotate-1' },
              { src: PHOTO_STAGE, alt: 'Primatotterinas end of year show', rot: 'rotate-1' },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.12 }}
                className={`${p.rot} bg-white p-2 rounded-2xl shadow-lg shadow-pink-100 hover:rotate-0 hover:scale-105 transition-transform`}
              >
                <img src={p.src} alt={p.alt} className="w-full h-40 sm:h-52 object-cover object-top rounded-xl" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * (i + 1) }}>
                <Card className={`${f.bg} ${f.border} border-2 rounded-3xl h-full shadow-sm hover:shadow-md hover:-translate-y-1 transition-all`}>
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-sm mb-4">
                      <f.icon className={`w-7 h-7 ${f.iconColor}`} />
                    </div>
                    <h3 className="font-bold text-neutral-800 mb-2">{f.title}</h3>
                    <p className="text-neutral-500 text-sm">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={c.section_image || PHOTO_TUTUS}
                alt="Primatotterinas Class"
                className="aspect-[4/3] w-full object-cover object-top rounded-3xl shadow-xl shadow-pink-100"
              />
              <div className="absolute -bottom-4 -right-3 bg-[#FF007F] text-white text-sm font-bold px-4 py-2 rounded-full rotate-3 shadow-lg">
                So much fun! ✨
              </div>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-neutral-800 mb-5">
                {c.about_title}
              </h2>
              <p className="text-neutral-600 mb-4">{c.about_p1}</p>
              <p className="text-neutral-600 mb-6">{c.about_p2}</p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-pink-50 border-2 border-pink-100 rounded-2xl">
                  <Clock className="w-6 h-6 text-[#FF007F] mx-auto mb-2" />
                  <p className="font-bold text-neutral-800">{c.class_duration}</p>
                  <p className="text-neutral-500 text-xs">Per class</p>
                </div>
                <div className="p-4 bg-purple-50 border-2 border-purple-100 rounded-2xl">
                  <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="font-bold text-neutral-800">{c.class_size}</p>
                  <p className="text-neutral-500 text-xs">Class sizes</p>
                </div>
                <div className="p-4 bg-amber-50 border-2 border-amber-100 rounded-2xl">
                  <Calendar className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                  <p className="font-bold text-neutral-800">{c.class_day}</p>
                  <p className="text-neutral-500 text-xs">Weekly</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What They'll Learn */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-neutral-800 mb-8 text-center">
            What Your Little One Will Learn
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {skills.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 p-3.5 bg-white border-2 border-pink-100 rounded-2xl shadow-sm"
              >
                <span className="w-7 h-7 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <Star className="w-3.5 h-3.5 text-[#FF007F]" />
                </span>
                <span className="text-neutral-700">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-14 px-4 bg-gradient-to-r from-pink-50 to-purple-50">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-white border-2 border-pink-200 rounded-3xl shadow-xl shadow-pink-100">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-black text-neutral-800 mb-1">Primatotterinas</h2>
              <p className="text-neutral-500 mb-6">Monthly subscription</p>
              <div className="mb-6">
                <span className="text-5xl font-black text-[#FF007F]">£{c.price}</span>
                <span className="text-neutral-500">/month</span>
              </div>
              <ul className="space-y-3 text-left mb-8 max-w-sm mx-auto">
                {priceFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-neutral-600">
                    <Star className="w-4 h-4 text-[#FF007F] flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <BookTrialButton testId="prima-book-trial-pricing" size="md" />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Parent Info */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black text-neutral-800 mb-4">
            Information for Parents
          </h2>
          <p className="text-neutral-600 mb-8 max-w-2xl mx-auto">
            {c.parent_info}
          </p>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <Card className="bg-pink-50 border-2 border-pink-100 rounded-3xl">
              <CardContent className="p-6">
                <h3 className="font-bold text-neutral-800 mb-2">What to Wear</h3>
                <p className="text-neutral-600 text-sm">{c.what_to_wear}</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-2 border-purple-100 rounded-3xl">
              <CardContent className="p-6">
                <h3 className="font-bold text-neutral-800 mb-2">What to Bring</h3>
                <p className="text-neutral-600 text-sm">{c.what_to_bring}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 text-center bg-gradient-to-b from-[#fff7fb] to-pink-100">
        <Sparkles className="w-8 h-8 text-[#FF007F] mx-auto mb-4" />
        <h2 className="text-2xl md:text-3xl font-black text-neutral-800 mb-3">
          {c.cta_title}
        </h2>
        <p className="text-neutral-600 mb-7 max-w-2xl mx-auto">
          {c.cta_subtitle}
        </p>
        <BookTrialButton testId="prima-book-trial-footer" />
      </section>
    </div>
  );
};

export default Primatotterinas;
